// internal deps
import { useCallback, useContext, useEffect, useRef, useState } from "react";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import {
    getCouponAutoAppliedFlag,
    getCouponSuccessCard,
    getSessionId,
    getTradeDiscountRewardAmount,
    getTradeDiscountSuccessCard,
    isDealRushActive,
    isExpiryCashbackActive,
    isGenericDelightPopupActive,
    isInstantCouponActive,
    isPocketHeroCashbackActive,
} from "@cart-client/helpers/Cart";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
// types & constants
import { DELIGHT_POPUP_IMPRESSION_FLAG, DEAL_RUSH_POPUP_IMPRESSION_FLAG } from "@cart-client/constants/index";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import {
    CouponModalIds,
    GlobalOverlayPriority,
    OverlayType,
    TDSuccessCardType,
    GenericTDSuccessCardType,
} from "@swiggy-private/cart-types";

//sessionStorage
import { CartOverlaysContext, useSessionStorage } from "@swiggy-private/checkout-core";

// localStorage
import { LocalStorage } from "@swiggy-private/core-ui";

// contexts
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";
import { shouldRegisterOverlay } from "./helpers/AutoApplyCallout";

const useAutoApplyCallout = (cartResponse: ReviewedCart | null, toggleCouponModal: VoidFunction) => {
    const [showDelightPopup, setShowDelightPopup] = useState(false);
    const [delightPopupImpression, setDelightPopupImpression] = useSessionStorage(DELIGHT_POPUP_IMPRESSION_FLAG, false);

    const couponSuccessData = getCouponSuccessCard(cartResponse);
    const isCouponAutoApplied = getCouponAutoAppliedFlag(cartResponse);

    const tradeDiscountSuccessInfo = getTradeDiscountSuccessCard(cartResponse);
    const tradeDiscountRewardAmount = getTradeDiscountRewardAmount(cartResponse);
    const registerInstantCoupon = useRef(false);
    const registerPocketHeroCoupon = useRef(false);
    const registerGiftingCoupon = useRef(false);
    const registerDealRush = useRef(false);
    const registerGenericDelightRewardOffer = useRef(false);

    const { data: cartFlowData } = useContext(CartFlowContext);
    const isGiftingCart = isGiftingCartSource(cartFlowData?.cartSource);

    const { registerOverlays, enabledOverlayId, onCloseOverlay } = useContext(CartOverlaysContext);

    const { togglingDelightPopup } = useContext(UpdateCartContext);

    const handleShowDelightPopup = useCallback(() => {
        setShowDelightPopup(true);
        togglingDelightPopup();
    }, [togglingDelightPopup]);

    const handleCloseDelightPopup = useCallback(() => {
        setShowDelightPopup(false);
        togglingDelightPopup();
        onCloseOverlay();
    }, [togglingDelightPopup, onCloseOverlay]);

    useEffect(() => {
        // Auto Applied Instant-Coupon
        if (!isEmpty(couponSuccessData) && isCouponAutoApplied && !registerInstantCoupon.current) {
            registerOverlays({
                overlayType: OverlayType.MODAL,
                id: CouponModalIds.INSTANT_COUPON,
                priority: GlobalOverlayPriority.AUTO_APPLIED_COUPON,
            });
            registerInstantCoupon.current = true;
        }

        if (
            shouldRegisterOverlay(tradeDiscountSuccessInfo, delightPopupImpression, registerPocketHeroCoupon) &&
            // Auto Applied PocketHero Cashback
            (tradeDiscountSuccessInfo?.td_success_card_type === TDSuccessCardType.OFFER_CARD_TYPE_POCKET_HERO ||
                // Auto Applied Expiring Cashback
                (!isEmpty(tradeDiscountRewardAmount) && tradeDiscountSuccessInfo?.is_reward_offer))
        ) {
            registerOverlays({
                overlayType: OverlayType.MODAL,
                id:
                    tradeDiscountSuccessInfo?.td_success_card_type === TDSuccessCardType.OFFER_CARD_TYPE_POCKET_HERO
                        ? CouponModalIds.POCKET_HERO_CASHBACK
                        : CouponModalIds.EXPIRY_CASHBACK,
                priority: GlobalOverlayPriority.AUTO_APPLIED_COUPON,
            });
            registerPocketHeroCoupon.current = true;
        }

        // Auto Applied generic delight reward offer
        if (
            shouldRegisterOverlay(
                tradeDiscountSuccessInfo,
                delightPopupImpression,
                registerGenericDelightRewardOffer,
                GenericTDSuccessCardType,
            )
        ) {
            registerOverlays({
                overlayType: OverlayType.MODAL,
                id: CouponModalIds.GENERIC_DELIGHT_POPUP,
                priority: GlobalOverlayPriority.AUTO_APPLIED_COUPON,
            });
            registerGenericDelightRewardOffer.current = true;
        }

        // Auto Applied Deal Rush - uses session ID for frequency capping (once per backend session)
        const currentSessionId = getSessionId();
        const storedDealRushSessionId = LocalStorage.getItem(DEAL_RUSH_POPUP_IMPRESSION_FLAG, "") ?? "";
        const dealRushImpressionShown =
            !isEmpty(storedDealRushSessionId) && storedDealRushSessionId === currentSessionId;
        if (
            shouldRegisterOverlay(
                tradeDiscountSuccessInfo,
                dealRushImpressionShown,
                registerDealRush,
                TDSuccessCardType.OFFER_CARD_TYPE_DEAL_RUSH,
            )
        ) {
            registerOverlays({
                overlayType: OverlayType.MODAL,
                id: CouponModalIds.DEAL_RUSH,
                priority: GlobalOverlayPriority.AUTO_APPLIED_COUPON,
            });
            registerDealRush.current = true;
        }
    }, [
        couponSuccessData,
        isCouponAutoApplied,
        tradeDiscountSuccessInfo,
        tradeDiscountRewardAmount,
        delightPopupImpression,
        registerOverlays,
        toggleCouponModal,
    ]);

    useEffect(() => {
        if (!isEmpty(couponSuccessData) && !registerGiftingCoupon.current && isGiftingCart) {
            registerOverlays({
                overlayType: OverlayType.MODAL,
                id: CouponModalIds.GIFTING_GRATIFICATION_POPUP,
                priority: GlobalOverlayPriority.GIFTING_GRATIFICATION_POPUP,
            });
            registerGiftingCoupon.current = true;
        }
    }, [couponSuccessData, registerOverlays, isGiftingCart]);

    useEffect(() => {
        if (
            !delightPopupImpression &&
            (isPocketHeroCashbackActive(enabledOverlayId) ||
                isExpiryCashbackActive(enabledOverlayId) ||
                isGenericDelightPopupActive(enabledOverlayId))
        ) {
            setDelightPopupImpression(true);
            handleShowDelightPopup();
        } else if (!delightPopupImpression && isInstantCouponActive(enabledOverlayId)) {
            setDelightPopupImpression(true);
            toggleCouponModal();
        }

        // Deal Rush uses localStorage with session id for frequency capping
        const currentSessionId = getSessionId();
        const storedDealRushSessionId = LocalStorage.getItem(DEAL_RUSH_POPUP_IMPRESSION_FLAG, "") ?? "";
        const dealRushImpressionShown =
            !isEmpty(storedDealRushSessionId) && storedDealRushSessionId === currentSessionId;
        if (!dealRushImpressionShown && isDealRushActive(enabledOverlayId)) {
            LocalStorage.setItem(DEAL_RUSH_POPUP_IMPRESSION_FLAG, currentSessionId);
            handleShowDelightPopup();
        }
    }, [
        enabledOverlayId,
        handleShowDelightPopup,
        toggleCouponModal,
        setDelightPopupImpression,
        delightPopupImpression,
    ]);

    return {
        showDelightPopup,
        couponSuccessData,
        isCouponAutoApplied,
        tradeDiscountSuccessInfo,
        handleShowDelightPopup,
        handleCloseDelightPopup,
    };
};

export default useAutoApplyCallout;
